import React, { createContext, useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import { PiCaretRight, PiLockSimple, PiInfo } from "react-icons/pi";

export const SettingsContext = createContext({
  workspaceName: "",
  orgRole: null,
  roleKnown: false,
  canManage: false,
  overlayEl: null,
  setDirty: () => {},
});

export const useSettings = () => useContext(SettingsContext);

export const PageHeader = ({ scope, title, description, action }) => {
  const { workspaceName } = useSettings();
  const crumb = scope ?? workspaceName;
  return (
    <div className="flex items-end gap-6 mb-6 font-outfit">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-[13px] text-[#71717A]">
          {crumb && (
            <>
              <span className="truncate">{crumb}</span>
              <PiCaretRight className="text-[12px] shrink-0" />
            </>
          )}
          <span className="text-[#3F3F46]">{title}</span>
        </div>
        <h1 className="mt-2 mb-1 text-[24px] font-semibold text-[#18101C]">{title}</h1>
        {description && (
          <p className="m-0 text-[14px] text-[#71717A] max-w-[640px]">{description}</p>
        )}
      </div>
      {action}
    </div>
  );
};

export const PrimaryButton = ({ icon: Icon, children, className = "", ...props }) => (
  <button
    type="button"
    className={`h-[38px] px-4 inline-flex items-center gap-2 shrink-0 font-outfit text-[14px] font-medium text-[#FAFAFA] bg-[#A04A97] hover:bg-[#8E4186] rounded-[8px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    {...props}
  >
    {Icon && <Icon className="text-[16px]" />}
    {children}
  </button>
);

export const SecondaryButton = ({ icon: Icon, children, className = "", ...props }) => (
  <button
    type="button"
    className={`h-9 px-3.5 inline-flex items-center gap-2 font-outfit text-[14px] font-medium text-[#3F3F46] bg-white border border-black/20 rounded-[8px] hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    {...props}
  >
    {Icon && <Icon className="text-[16px]" />}
    {children}
  </button>
);

export const GhostButton = ({ icon: Icon, children, className = "", ...props }) => (
  <button
    type="button"
    className={`h-9 px-3.5 inline-flex items-center gap-2 font-outfit text-[14px] font-medium text-[#3F3F46] bg-transparent border border-transparent rounded-[8px] hover:bg-black/[0.04] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    {...props}
  >
    {Icon && <Icon className="text-[16px]" />}
    {children}
  </button>
);

export const SettingRow = ({ label, help, children }) => (
  <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] gap-3 md:gap-8 py-6 border-t border-black/[0.08] font-outfit">
    <div>
      <div className="text-[14px] font-semibold text-[#18101C]">{label}</div>
      {help && <div className="mt-1 text-[13px] text-[#71717A]">{help}</div>}
    </div>
    <div className="min-w-0">{children}</div>
  </div>
);

export const TextInput = React.forwardRef(({ className = "", ...props }, ref) => (
  <input
    ref={ref}
    className={`w-full max-w-[420px] h-10 px-3 font-outfit text-[15px] font-medium text-[#18101C] bg-white border border-black/15 rounded-[8px] outline-none placeholder:text-[#A1A1AA] focus:border-[#8B55DD] focus:shadow-[0_0_0_3px_rgba(139,85,221,0.15)] transition-shadow ${className}`}
    {...props}
  />
));

export const LockedField = ({ children, className = "" }) => (
  <div
    className={`w-full max-w-[420px] h-10 px-3 flex items-center gap-2 font-outfit text-[15px] font-medium text-[#3F3F46] bg-[#F4F4F5] border border-black/[0.08] rounded-[8px] ${className}`}
  >
    <span className="flex-1 min-w-0 truncate">{children}</span>
    <PiLockSimple className="text-[#A1A1AA] shrink-0" />
  </div>
);

export const Toggle = ({ checked, onChange, disabled, title, ...props }) => (
  <span title={title} className="inline-flex shrink-0">
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={title}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      className={`relative inline-flex shrink-0 w-9 h-5 rounded-full transition-colors duration-150 disabled:pointer-events-none ${checked ? "bg-[#A04A97]" : "bg-[#D4D4D8]"} ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      {...props}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.2)] transition-transform duration-150 ${checked ? "translate-x-4" : ""}`}
      />
    </button>
  </span>
);

export const ListCard = ({ children, className = "" }) => (
  <div className={`border border-black/10 rounded-[12px] overflow-hidden bg-white font-outfit ${className}`}>
    {children}
  </div>
);

export const ListRow = ({ children, off, compact, className = "" }) => (
  <div
    className={`flex items-center gap-4 px-[18px] ${compact ? "py-2" : "py-3.5"} bg-white [&+&]:border-t [&+&]:border-black/[0.07] ${off ? "opacity-[0.45]" : ""} ${className}`}
  >
    {children}
  </div>
);

const PILL_TONES = {
  green: "bg-[rgba(22,163,74,0.1)] text-[#15803D]",
  purple: "bg-[rgba(139,85,221,0.1)] text-[#7C3AED]",
  neutral: "bg-[#F4F4F5] text-[#52525B]",
};

export const StatusPill = ({ tone = "neutral", children, className = "" }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-medium whitespace-nowrap ${PILL_TONES[tone]} ${className}`}
  >
    {children}
  </span>
);

export const DangerBox = ({ title, description, actionLabel, onAction, neutral }) => (
  <div
    className={`mt-6 p-5 rounded-[12px] flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 font-outfit border ${neutral ? "border-black/10" : "border-[rgba(226,25,25,0.2)] bg-[rgba(226,25,25,0.03)]"}`}
  >
    <div className="flex-1">
      <div className="text-[15px] font-semibold text-[#18101C]">{title}</div>
      <div className="mt-1 text-[13px] text-[#71717A]">{description}</div>
    </div>
    {actionLabel && (
      <button
        type="button"
        onClick={onAction}
        className={`h-9 px-3.5 shrink-0 self-start sm:self-auto font-outfit text-[14px] font-medium text-[#E21919] bg-white border border-[rgba(226,25,25,0.4)] rounded-[8px] transition-colors ${neutral ? "hover:bg-[rgba(226,25,25,0.06)]" : "hover:bg-[#E21919] hover:text-white"}`}
      >
        {actionLabel}
      </button>
    )}
  </div>
);

export const ViewOnlyBanner = ({ icon: Icon, children, pill = "View only" }) => (
  <div className="flex items-center gap-3 px-4 py-3 -mt-2 mb-6 max-w-[860px] rounded-[10px] bg-[#F4F4F5] border border-black/[0.06] font-outfit">
    {Icon && <Icon className="text-[18px] text-[#52525B] shrink-0" />}
    <span className="flex-1 text-[14px] text-[#3F3F46]">{children}</span>
    <span className="shrink-0 text-[12px] font-medium text-[#52525B] bg-white border border-black/10 px-2 py-0.5 rounded-full">
      {pill}
    </span>
  </div>
);

export const SaveBar = ({ show, onDiscard, onSave, saving }) => {
  const { overlayEl, setDirty } = useSettings();
  useEffect(() => {
    setDirty(!!show);
    return () => setDirty(false);
  }, [show, setDirty]);
  if (!show || !overlayEl) return null;
  return createPortal(
    <div className="pointer-events-auto absolute left-4 right-4 md:left-10 md:right-10 bottom-6 max-w-[760px] flex items-center gap-3 py-2.5 pr-2.5 pl-[18px] bg-[#2C1338] rounded-[12px] shadow-[0_8px_24px_rgba(44,19,56,0.25)] font-outfit">
      <PiInfo className="text-[18px] text-[#C9A9D6] shrink-0" />
      <span className="flex-1 text-[14px] text-[#FAFAFA]">You have unsaved changes</span>
      <button
        type="button"
        onClick={onDiscard}
        disabled={saving}
        className="h-[34px] px-3.5 text-[14px] font-medium text-[#C7C7C7] bg-transparent rounded-[8px] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
      >
        Discard
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="h-[34px] px-4 text-[14px] font-medium text-[#FAFAFA] bg-[#A04A97] rounded-[8px] hover:bg-[#8E4186] transition-colors disabled:opacity-60"
      >
        {saving ? "Saving..." : "Save changes"}
      </button>
    </div>,
    overlayEl
  );
};

export const initialsOf = (name, max = 2) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, max)
    .map((w) => w[0].toUpperCase())
    .join("") || "W";
